import { makeAutoObservable, runInAction } from 'mobx';
import { signalRService, SignalRConnectionState } from 'src/services/signalRService';
import { keepAliveService } from 'src/services/keepAliveService';
import { getLocalInfo } from 'src/utils/encrypt';

export class SignalRStore {
  // Connection state
  connectionState: SignalRConnectionState = {
    isConnected: false,
    isConnecting: false,
    isReconnecting: false,
  };

  constructor() {
    makeAutoObservable(this);
    this.initializeSignalR();
    this.setupVisibilityHandler();
  }

  private setupVisibilityHandler(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && !this.isConnected && !this.isConnecting && !this.isReconnecting) {
          const accessToken = getLocalInfo('accessToken');
          if (accessToken) {
            this.connect();
          }
        }
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => {
        if (!this.isConnected && !this.isConnecting && !this.isReconnecting) {
          const accessToken = getLocalInfo('accessToken');
          if (accessToken) {
            this.connect();
          }
        }
      });
    }
  }

  private initializeSignalR(): void {
    signalRService.onConnectionStateChange((state) => {
      runInAction(() => {
        this.connectionState = state;
        
        if (state.isConnected) {
          keepAliveService.startKeepAlive();
        } else if (!state.isConnecting && !state.isReconnecting) {
          keepAliveService.stopKeepAlive();
        }
      });
    });

    signalRService.onRemoveFromWaitingList((data: { doctorId: number; consultationId: number; status: string }) => {
      runInAction(() => {
        this.triggerRemoveFromWaitingList(data);
      });
    });

    signalRService.onRemoveFromConsultedList((data: { doctorId: number; consultationId: number; status: string }) => {
      runInAction(() => {
        this.triggerRemoveFromConsultedList(data);
      });
    });

    signalRService.onRemoveFromScheduledList((data: { doctorId: number; consultationId: number; status: string }) => {
      runInAction(() => {
        this.triggerRemoveFromScheduledList(data);
      });
    });

    signalRService.onAddToWaitingList((patientDataList: any[]) => {
      runInAction(() => {
        this.triggerAddToWaitingList(patientDataList);
      });
    });

    signalRService.onAddToScheduleList((patientDataList: any[]) => {
      try {
        runInAction(() => {
          this.triggerAddToScheduleList(patientDataList);
        });
      } catch (error) {
        console.error('Error handling addToScheduleList in store:', error);
      }
    });

    signalRService.onUserJoined((userData: any) => {
      runInAction(() => {
        this.triggerUserJoined(userData);
      });
    });
  }

  private removeFromWaitingListCallbacks: ((data: { doctorId: number; consultationId: number; status: string }) => void)[] = [];

  public onRemoveFromWaitingList(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    this.removeFromWaitingListCallbacks.push(callback);
  }

  private triggerRemoveFromWaitingList(data: { doctorId: number; consultationId: number; status: string }): void {
    this.removeFromWaitingListCallbacks.forEach(callback => callback(data));
  }

  private removeFromConsultedListCallbacks: ((data: { doctorId: number; consultationId: number; status: string }) => void)[] = [];

  public onRemoveFromConsultedList(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    this.removeFromConsultedListCallbacks.push(callback);
  }

  private triggerRemoveFromConsultedList(data: { doctorId: number; consultationId: number; status: string }): void {
    this.removeFromConsultedListCallbacks.forEach(callback => callback(data));
  }

  private removeFromScheduledListCallbacks: ((data: { doctorId: number; consultationId: number; status: string }) => void)[] = [];

  public onRemoveFromScheduledList(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    this.removeFromScheduledListCallbacks.push(callback);
  }

  private triggerRemoveFromScheduledList(data: { doctorId: number; consultationId: number; status: string }): void {
    this.removeFromScheduledListCallbacks.forEach(callback => callback(data));
  }

  private addToWaitingListCallbacks: ((patientDataList: any[]) => void)[] = [];

  public onAddToWaitingList(callback: (patientDataList: any[]) => void): void {
    this.addToWaitingListCallbacks.push(callback);
  }

  private triggerAddToWaitingList(patientDataList: any[]): void {
    this.addToWaitingListCallbacks.forEach(callback => callback(patientDataList));
  }

  private addToScheduleListCallbacks: ((patientDataList: any[]) => void)[] = [];

  public onAddToScheduleList(callback: (patientDataList: any[]) => void): void {
    this.addToScheduleListCallbacks.push(callback);
  }

  private triggerAddToScheduleList(patientDataList: any[]): void {
    this.addToScheduleListCallbacks.forEach(callback => {
      try {
        callback(patientDataList);
      } catch (error) {
        console.error('Error in addToScheduleList callback:', error);
      }
    });
  }

  private userJoinedCallbacks: ((userData: any) => void)[] = [];

  public onUserJoined(callback: (userData: any) => void): void {
    this.userJoinedCallbacks.push(callback);
  }

  private triggerUserJoined(userData: any): void {
    this.userJoinedCallbacks.forEach(callback => callback(userData));
  }

  async connect(): Promise<void> {
    try {
      await signalRService.connect();
    } catch (error) {
      console.error('Failed to connect to SignalR:', error);
    }
  }

  async joinGroups(groups: string[]): Promise<void> {
    try {
      await signalRService.joinGroups(groups);
    } catch (error) {
      console.error('Failed to join groups:', error);
    }
  }

  async leaveGroups(groups: string[]): Promise<void> {
    try {
      await signalRService.leaveGroups(groups);
    } catch (error) {
      console.error('Failed to leave groups:', error);
    }
  }

  async disconnect(): Promise<void> {
    try {
      keepAliveService.stopKeepAlive();
      await signalRService.disconnect();
    } catch (error) {
      console.error('Failed to disconnect from SignalR:', error);
    }
  }

  get isConnected(): boolean {
    return this.connectionState.isConnected;
  }

  get isConnecting(): boolean {
    return this.connectionState.isConnecting;
  }

  get isReconnecting(): boolean {
    return this.connectionState.isReconnecting;
  }

  get connectionId(): string | undefined {
    return this.connectionState.connectionId;
  }
}

